import { Injectable, OnDestroy, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import {
  GroupedResults,
  TDetailsQuery,
  TResultsBot,
  TResultsPagination,
} from '@core/interfaces/TResultsItem';

import { BotsService } from '@shared/services/bots.service';
import { ResultsService } from '@shared/services/results.service';

@Injectable({
  providedIn: 'root',
})
export class ResultDataLoaderService implements OnDestroy {
  readonly groupedResults = signal<GroupedResults[]>([]);

  constructor(
    private _resultsService: ResultsService,
    private _botsService: BotsService
  ) {}

  /** Global loading flag for grouped results (sidebar). Exposed as a signal so the shell updates reliably. */
  readonly isLoading = signal(false);
  private _subs: Subscription[] = [];
  private _loadToken = 0;
  public pagination: TResultsPagination | null = null;

  public loadDateGroups(page = 1, limit = 100): void {
    const token = ++this._loadToken;
    this.isLoading.set(true);
    this.groupedResults.set([]);
    const sub = this._resultsService.getResultsDatesGroups(page, limit).subscribe({
      next: response => {
        if (token !== this._loadToken) return;
        const groups: GroupedResults[] = response.results.map(result => ({
          detailsQuery: { key: 'date' as const, value: result.date },
          name: new Date(result.date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          }),
          items: [],
          count: result.count,
          isOpened: false,
        }));
        this.groupedResults.set(groups);
        this.pagination = response.pagination;
        this.isLoading.set(false);
      },
      error: err => {
        if (token !== this._loadToken) return;
        console.error(err);
        this.isLoading.set(false);
      },
    });
    this._subs.push(sub);
  }

  public loadBotGroups(): void {
    const token = ++this._loadToken;
    this.isLoading.set(true);
    this.groupedResults.set([]);
    this._subs.push(
      this._botsService.fetchRawBotsData().subscribe({
        next: results => {
          if (token !== this._loadToken) return;
          const groups: GroupedResults[] = results
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(bot => ({
            detailsQuery: { key: 'botID' as const, value: bot._id },
            name: bot.name,
            items: [],
            count: 0,
            isOpened: false,
            isLoading: false,
          }));
          this.groupedResults.set(groups);
          this.isLoading.set(false);
        },
        error: () => {
          if (token !== this._loadToken) return;
          this.isLoading.set(false);
        },
      })
    );
  }

  public loadFavoritesGroups(): void {
    const token = ++this._loadToken;
    this.isLoading.set(true);
    this.groupedResults.set([]);

    this._subs.push(
      this._resultsService.fetchResults(1, 500).subscribe({
        next: response => {
          if (token !== this._loadToken) return;
          const starredConversations = response.results.filter(
            (result: TResultsBot) => result.favorite
          );
          const groups: GroupedResults[] =
            starredConversations.length > 0
              ? [
                  {
                    detailsQuery: { key: 'favorite' as const, value: 'true' },
                    name: 'Starred Conversations',
                    items: starredConversations,
                    count: starredConversations.length,
                    isOpened: true,
                    isLoading: false,
                  },
                ]
              : [
                  {
                    detailsQuery: { key: 'none' as const, value: 'none' },
                    name: 'No favorites found - Star some conversations or messages to see them here!',
                    items: [],
                    count: 0,
                    isOpened: false,
                    isLoading: false,
                  },
                ];
          this.groupedResults.set(groups);
          this.isLoading.set(false);
        },
        error: err => {
          if (token !== this._loadToken) return;
          console.error('Error loading favorites:', err);
          this.groupedResults.set([
            {
              detailsQuery: { key: 'none' as const, value: 'none' },
              name: 'Error loading favorites',
              items: [],
              count: 0,
              isOpened: false,
              isLoading: false,
            },
          ]);
          this.isLoading.set(false);
        },
      })
    );
  }

  /** Remove a conversation from all groups (e.g. after delete). Updates the signal so the nav re-renders. */
  public removeConversationFromGroups(conversationId: string): void {
    this.groupedResults.update(groups =>
      groups.map(g => {
        const newItems = g.items.filter(item => item._id !== conversationId);
        const removed = newItems.length < g.items.length;
        return {
          ...g,
          items: newItems,
          ...(removed && g.count != null && { count: Math.max(0, g.count - 1) }),
        };
      })
    );
  }

  /** Set items for a group (e.g. when expanding a group). */
  public setGroupItems(detailsQuery: TDetailsQuery, items: TResultsBot[]): void {
    this.groupedResults.update(groups =>
      groups.map(g =>
        g.detailsQuery.key === detailsQuery.key &&
        g.detailsQuery.value === detailsQuery.value
          ? { ...g, items, isLoading: false }
          : g
      )
    );
  }

  /** Set loading state for a group. */
  public setGroupLoading(detailsQuery: TDetailsQuery, isLoading: boolean): void {
    this.groupedResults.update(groups =>
      groups.map(g =>
        g.detailsQuery.key === detailsQuery.key &&
        g.detailsQuery.value === detailsQuery.value
          ? { ...g, isLoading }
          : g
      )
    );
  }

  /** Update a group by detailsQuery (e.g. toggle isOpened). */
  public updateGroup(
    detailsQuery: TDetailsQuery,
    patch: Partial<Pick<GroupedResults, 'isOpened' | 'isLoading'>>
  ): void {
    this.groupedResults.update(groups =>
      groups.map(g =>
        g.detailsQuery.key === detailsQuery.key &&
        g.detailsQuery.value === detailsQuery.value
          ? { ...g, ...patch }
          : g
      )
    );
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }
}
